import { useEffect, useRef, useState } from 'react';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { CalendarIcon, ChevronDownIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import { dateOfInput } from '@/lib/clock';
import { cx } from '@/lib/cx';
import type { DateRange, RangePreset } from './useScheduleParams';
import { RANGE_PRESETS, isDayAligned, isWholeDay } from './useScheduleParams';
import styles from './SchedulePage.module.css';

/**
 * The period the board is showing, and the two ways of changing it.
 *
 * One control, because a period is one fact. A named period and a pair of dates are
 * two ways of saying it, not two settings — and when they were two controls (a menu
 * here, two date fields in the Scheduled column) the reader had to hold in their head
 * which of them the board was currently obeying.
 *
 * The trigger is the readout as well, and it says what was chosen rather than what
 * that worked out to: "Last 7 days", not `14.08.2026 — 20.08.2026`. That is only
 * possible because the URL stores the NAME — see useScheduleParams. A hand-set period
 * has no name, so it reads as its two ends.
 */

interface RangePickerProps {
  /** The named period in force, or null when the dates were set by hand. */
  preset: RangePreset | null;
  /** The instants in force, whether they came from a name or from the fields. */
  range: DateRange;
  onPickPreset: (preset: RangePreset) => void;
  onPickRange: (range: DateRange) => void;
}

export const RangePicker = ({ preset, range, onPickPreset, onPickRange }: RangePickerProps) => {
  const { t, formatDateLong, formatDate, formatDateTime } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  /**
   * What the four boxes show.
   *
   * Still state and not the `range` prop, even though a usable edit is applied the
   * moment it is made: a half-finished date reads as '' out of the input, and a
   * period with no start is not something to apply. So the fields hold what is being
   * entered, and `commit` below decides when that has become a period.
   */
  const [draft, setDraft] = useState<DateRange>(range);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  const close = (returnFocus: boolean) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const open = () => {
    // Seeded from what is in force, so the fields start where the board is rather than
    // empty. Done here rather than in an effect: opening is an event, not a state the
    // component has to observe.
    setDraft(range);
    setIsOpen(true);
  };

  /** A period has to have a start, an end, and them in that order. */
  const isDraftValid =
    draft.dateFrom !== '' && draft.dateTo !== '' && draft.dateFrom <= draft.dateTo;

  /**
   * One end changed: shown always, applied when the pair adds up to a period.
   *
   * There is no Apply button, because there was nothing for one to do that this
   * cannot. A day is picked in one click and the board follows it — the same as
   * pressing a named period, which never needed confirming either.
   *
   * The guard is not about typing speed. A date box reports '' until the whole date
   * is valid, so an end mid-entry is genuinely empty, and `from` after `to` is
   * reachable through the times even though the calendars bound each other. Neither
   * is a period, so neither is applied; the boxes keep showing it and the hint below
   * says why the board has not moved.
   */
  const commit = (end: 'dateFrom' | 'dateTo', value: string) => {
    const next = { ...draft, [end]: value };
    setDraft(next);

    if (next.dateFrom === '' || next.dateTo === '' || next.dateFrom > next.dateTo) return;
    onPickRange(next);
  };

  // The keyboard lands on the first period rather than being left on the trigger,
  // which is what makes the menu usable without a mouse.
  useEffect(() => {
    if (isOpen) firstOptionRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    /*
      Closing, and nothing more: every usable edit was applied as it was made, so
      there is no draft left here to keep or to throw away. That is the point of
      having no Apply button — the click that dismisses this menu cannot also be the
      click that loses the period, which is exactly what it used to be.

      Clicks inside the browser's date picker do not come through here: that popup
      is not in the page's DOM and produces no page pointer events, so picking a day
      cannot close this menu out from under it.
    */
    const onPointerDown = (event: PointerEvent) => {
      if (boxRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  /**
   * What the trigger says.
   *
   * A name if there is one. Otherwise one day reads as that day, several whole days as
   * two dates, and anything else as two timestamps — a range that shows `00:00` and
   * `23:59` spends the reader's attention on its two least informative numbers.
   */
  const label =
    preset !== null
      ? t(`schedule.range.${preset}`)
      : isWholeDay(range)
        ? formatDateLong(range.dateFrom)
        : isDayAligned(range)
          ? `${formatDate(range.dateFrom)} — ${formatDate(range.dateTo)}`
          : `${formatDateTime(range.dateFrom)} — ${formatDateTime(range.dateTo)}`;

  /** No name means the period was set in the fields below, so they are the current one. */
  const isCustom = preset === null;

  return (
    <div className={styles.range} ref={boxRef}>
      <button
        type="button"
        ref={triggerRef}
        className={cx(styles.rangeTrigger, isOpen && styles.rangeTriggerOpen)}
        onClick={() => (isOpen ? close(false) : open())}
        aria-expanded={isOpen}
        aria-haspopup="true"
        // The instants, always: the name is the headline and this is the detail behind
        // it, which is exactly what a reader hovers a named period to find out.
        title={`${formatDateTime(range.dateFrom)} — ${formatDateTime(range.dateTo)}`}
      >
        <CalendarIcon className={styles.rangeIcon} />
        <span className={styles.rangeLabel}>{label}</span>
        <ChevronDownIcon className={styles.rangeCaret} />
      </button>

      {isOpen && (
        <div className={styles.rangeMenu} role="group" aria-label={t('schedule.range.label')}>
          <div className={styles.rangePresets}>
            {RANGE_PRESETS.map((option, index) => (
              <button
                key={option}
                type="button"
                ref={index === 0 ? firstOptionRef : undefined}
                className={cx(styles.rangeOption, option === preset && styles.rangeOptionCurrent)}
                // Pressed rather than selected: this is a menu of actions, and the one
                // in force is worth marking so the reader can see where they are.
                aria-pressed={option === preset}
                onClick={() => {
                  onPickPreset(option);
                  close(true);
                }}
              >
                {t(`schedule.range.${option}`)}
              </button>
            ))}
          </div>

          {/*
            Not a `<form>` any more: there is nothing to submit. It also could not
            have been submitted from the keyboard once the Apply button went — with
            no submit button, four date and time boxes are four fields that block
            implicit submission, so Enter would have done nothing at all. Enter is
            handled below instead, and means the same as clicking away: done here.

            The fields stay CONTROLLED. Now that each edit is applied, the URL does
            change under them — but only ever to what they just said, so there is
            nothing to fight: `range` and `draft` agree by the time it comes back.
          */}
          <div
            className={cx(styles.rangeForm, isCustom && styles.rangeFormCurrent)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              close(true);
            }}
          >
            {/* Marked when it is the period in force, the same as a named one above:
                the two halves of this menu are one choice, so exactly one of them is
                ever current. A hand-set period is what having no name means. */}
            <span className={cx(styles.rangeFormTitle, isCustom && styles.rangeFormTitleCurrent)}>
              {t('schedule.range.custom')}
            </span>

            {/*
              A date box and a time box per end, rather than one `datetime-local`:
              the combined field's picker stays open after the day because it still
              wants a time, and nothing on this side can close it. See
              components/ui/DateTimeInput.

              The bounds are the OTHER end's date, so the two cannot be crossed with
              the calendars. A same-day inversion — 14:00 to 06:00 — is still
              typeable, and is what the hint at the bottom is for.
            */}
            <div className={styles.rangeField}>
              <span className={styles.rangeFieldLabel}>{t('schedule.scheduledFrom')}</span>
              <DateTimeInput
                value={draft.dateFrom}
                onChange={(value) => commit('dateFrom', value)}
                defaultTime="00:00"
                label={t('schedule.scheduledFrom')}
                maxDate={dateOfInput(draft.dateTo) || undefined}
                inputClassName={cx(styles.rangeInput, isCustom && styles.rangeInputActive)}
              />
            </div>

            <div className={styles.rangeField}>
              <span className={styles.rangeFieldLabel}>{t('schedule.scheduledTo')}</span>
              <DateTimeInput
                value={draft.dateTo}
                onChange={(value) => commit('dateTo', value)}
                // A day chosen for the end of a period means the end of that day.
                defaultTime="23:59"
                label={t('schedule.scheduledTo')}
                minDate={dateOfInput(draft.dateFrom) || undefined}
                inputClassName={cx(styles.rangeInput, isCustom && styles.rangeInputActive)}
              />
            </div>

            {/* Why the board has not moved. It took the disabled Apply button's job:
                without either, an inverted pair would just sit there being ignored. */}
            {!isDraftValid && (
              <p className={styles.rangeHint} role="alert">
                {t('schedule.range.invalid')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
